import type { MultipartFailureKind, MultipartReconciliationVerdict, MultipartSessionStatus } from '@atlas/types'
import { isAxiosError } from 'axios'
import { apiErrorMessage } from '@/lib/api-error'
import type { UploadRecoveryAction } from '../../upload-recovery'

/**
 * The stable `code` on a multipart response, or `null`.
 *
 * Clients branch on this and never on the HTTP status: the contract reuses
 * `409` and `422` for outcomes with different client actions, so status alone
 * cannot decide anything. An unrecognized code is treated as terminal and
 * non-retryable, which the contract itself prescribes.
 */
export function multipartErrorCode(error: unknown): string | null {
	if (!isAxiosError(error)) return null
	const data = error.response?.data as { error?: { code?: unknown } } | undefined
	const code = data?.error?.code
	return typeof code === 'string' ? code : null
}

/** Everything a `409 MULTIPART_SESSION_CONFLICT` (and every other coded response) carries beyond its message. */
export function multipartErrorDetails(error: unknown): Record<string, unknown> {
	if (!isAxiosError(error)) return {}
	const data = error.response?.data as { error?: { details?: unknown } } | undefined
	const details = data?.error?.details
	return details && typeof details === 'object' ? (details as Record<string, unknown>) : {}
}

/** Was this a 404 on the presigned S3 URL — the `NoSuchUpload` an `UploadPart` gets after the multipart is gone? */
export function isNoSuchUploadResponse(error: unknown): boolean {
	if (!isAxiosError(error)) return false
	if (error.response?.status !== 404) return false
	const body = typeof error.response.data === 'string' ? error.response.data : ''
	// S3 answers XML on the data plane; a 404 there means the upload id is gone
	// whether or not the body could be read cross-origin.
	return body.includes('NoSuchUpload') || body === '' || !body.includes('<Code>')
}

/**
 * A multipart outcome the user has to act on, carrying what the surface needs
 * to say and offer. Internal fallback reasons never travel in `message` — they
 * belong in logs, not in front of a user.
 */
export class MultipartUploadError extends Error {
	constructor(
		message: string,
		public readonly kind: MultipartFailureKind,
		public readonly context: {
			code?: string | null
			status?: MultipartSessionStatus
			verdict?: MultipartReconciliationVerdict
			multipartSessionId?: string
			/** True when the local resumable metadata was cleared as part of this outcome. */
			resumeCleared?: boolean
			/**
			 * What the surface may offer for this outcome. Chosen here, at the throw
			 * site, because only the transport knows which server state the session
			 * is in — the manager and the tile just render what they are handed.
			 */
			actions?: UploadRecoveryAction[]
			/**
			 * Closes the server-side session this outcome left behind, for the
			 * actions that must release it first. Opaque to every consumer.
			 */
			releaseSession?: () => Promise<void>
		} = {},
	) {
		super(message)
		this.name = 'MultipartUploadError'
	}
}

/** Turns any thrown request failure into the user-facing text, without leaking transport detail. */
export function multipartMessage(error: unknown, fallback: string): string {
	return apiErrorMessage(error, fallback)
}
